import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Bookmark, Trash2, ArrowRight, Calendar, User, Building, Search, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';


const formatCurrency = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

const SavedProjections = ({ onLoad }) => {
  const [projecoes, setProjecoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchProjecoes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/calculadora/projecoes');
      setProjecoes(res.data);
    } catch (err) {
      console.error('Erro ao buscar projeções:', err);
      toast.error('Erro ao carregar projeções salvas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjecoes();
  }, [fetchProjecoes]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/calculadora/projecoes/${id}`);
      setProjecoes(prev => prev.filter(p => p.id !== id));
      toast.success('Projeção excluída');
    } catch (err) {
      toast.error('Erro ao excluir projeção');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = projecoes.filter(p => {
    const q = search.toLowerCase();
    return (
      (p.nome_cliente || '').toLowerCase().includes(q) ||
      (p.empreendimento || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <Loader2 size={32} className="animate-spin mb-3" />
        <p className="text-sm">Carregando projeções salvas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Search */}
      {projecoes.length > 0 && (
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-projecoes"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente ou empreendimento..."
            className="pl-10 h-11"
          />
        </div>
      )}

      {/* Empty State */}
      {projecoes.length === 0 && (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
            <Bookmark size={28} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            Nenhuma projeção salva
          </h3>
          <p className="text-slate-500">
            Faça uma simulação e clique em "Salvar Projeção" para guardá-la aqui.
          </p>
        </div>
      )}

      {/* No results for search */}
      {projecoes.length > 0 && filtered.length === 0 && (
        <div className="text-center py-10 text-slate-500">
          <p>Nenhuma projeção encontrada para "{search}"</p>
        </div>
      )}

      {/* Projection cards */}
      <div className="space-y-3">
        {filtered.map((proj) => {
          const createdAt = new Date(proj.created_at).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
          });
          const r = proj.result || {};

          return (
            <Card
              key={proj.id}
              data-testid={`saved-projection-${proj.id}`}
              className="bg-white border-slate-200 hover:border-primary/30 transition-colors"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <User size={14} className="text-emerald-600 flex-shrink-0" />
                        <span className="font-semibold text-sm truncate">
                          {proj.nome_cliente || 'Cliente não informado'}
                        </span>
                      </div>
                      <span className="text-slate-300">|</span>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Building size={14} className="text-emerald-600 flex-shrink-0" />
                        <span className="text-sm truncate">
                          {proj.empreendimento || 'Empreendimento não informado'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {createdAt}
                      </span>
                      <span>Prazo: {proj.form_data?.prazo_meses || '-'} meses</span>
                      <span>Val: {proj.form_data?.valorizacao_anual || '-'}%</span>
                    </div>

                    {/* Quick metrics */}
                    <div className="flex flex-wrap gap-3">
                      <div className="px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200">
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">Valor Compra</p>
                        <p className="text-xs font-mono font-semibold text-slate-800">
                          {formatCurrency(proj.form_data?.valor_compra)}
                        </p>
                      </div>
                      <div className="px-3 py-1.5 bg-emerald-50 rounded-md border border-emerald-200">
                        <p className="text-[9px] uppercase tracking-wider text-primary">Valor Imóvel Final</p>
                        <p className="text-xs font-mono font-semibold text-primary">
                          {formatCurrency(r.valor_imovel_final)}
                        </p>
                      </div>
                      <div className="px-3 py-1.5 bg-emerald-50 rounded-md border border-emerald-200">
                        <p className="text-[9px] uppercase tracking-wider text-primary">Lucro Líquido</p>
                        <p className="text-xs font-mono font-semibold text-primary">
                          {formatCurrency(r.lucro_liquido_final)}
                        </p>
                      </div>
                      <div className="px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200">
                        <p className="text-[9px] uppercase tracking-wider text-slate-500">Retorno s/ CI</p>
                        <p className="text-xs font-mono font-semibold text-slate-800">
                          {r.percentual_sobre_ci_final ? r.percentual_sobre_ci_final.toFixed(1) + '%' : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      data-testid={`btn-load-projection-${proj.id}`}
                      onClick={() => onLoad(proj)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <ArrowRight size={16} />
                      Carregar
                    </button>
                    <button
                      data-testid={`btn-delete-projection-${proj.id}`}
                      onClick={() => handleDelete(proj.id)}
                      disabled={deletingId === proj.id}
                      className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 text-red-600 text-sm font-medium rounded-lg border border-red-200 hover:border-red-400 transition-colors disabled:opacity-50"
                    >
                      {deletingId === proj.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Excluir
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SavedProjections;
